import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import {
  Users, CheckCircle, Clock, TrendingUp, FileText, BarChart3, ArrowRight, ArrowLeft,
  GraduationCap, Target, RefreshCw, Building2, Shield, Calendar, Loader2,
  AlertTriangle, Eye, Brain, Heart, Briefcase, Activity, XCircle, ChevronRight,
  Download, BookOpen, Newspaper, Search, Filter
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmt(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString(); } catch { return d; }
}

function RiskBadge({ level }) {
  const styles = {
    low: 'bg-emerald-100 text-emerald-700 border-emerald-300',
    moderate: 'bg-yellow-100 text-yellow-700 border-yellow-300',
    elevated: 'bg-orange-100 text-orange-700 border-orange-300',
    high: 'bg-red-100 text-red-700 border-red-300',
    critical: 'bg-red-200 text-red-800 border-red-400',
  };
  if (!level) return <Badge variant="outline" className="text-xs">Unassessed</Badge>;
  return <Badge className={`text-xs border ${styles[level] || 'bg-muted text-muted-foreground'}`}>{level.toUpperCase()}</Badge>;
}

/* ============================
   STAT CARD (clickable)
   ============================ */
function StatCard({ icon: Icon, label, value, color, onClick, testId, subtitle }) {
  const colors = {
    indigo: 'bg-indigo-50 text-indigo-600',
    sky: 'bg-sky-50 text-sky-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    violet: 'bg-violet-50 text-violet-600',
    red: 'bg-red-50 text-red-600',
    amber: 'bg-amber-50 text-amber-600',
    teal: 'bg-teal-50 text-teal-600',
  };
  const [bg, text] = (colors[color] || colors.indigo).split(' ');
  return (
    <Card
      className={`border-0 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md hover:scale-[1.02] transition-all' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      <CardContent className="p-4">
        <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center mb-2`}>
          <Icon className={`w-4 h-4 ${text}`} />
        </div>
        <p className="text-2xl font-bold text-foreground">{value}</p>
        <p className="text-xs text-muted-foreground">{label}</p>
        {subtitle && <p className="text-[10px] text-muted-foreground/70 mt-0.5">{subtitle}</p>}
        {onClick && <ChevronRight className="w-3.5 h-3.5 text-muted-foreground/40 absolute top-4 right-4" />}
      </CardContent>
    </Card>
  );
}

/* ============================
   PIPELINE TRACKER
   ============================ */
function PipelineTracker({ pipeline }) {
  if (!pipeline?.stages) return null;
  const stages = pipeline.stages;
  const current = pipeline.current_stage_index;

  return (
    <Card className="border-0 shadow-sm rounded-2xl" data-testid="pipeline-tracker">
      <CardContent className="pt-4 pb-4 px-3 sm:px-5">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp className="h-4 w-4 text-primary" />
          <h3 className="text-sm font-semibold" style={{ fontFamily: 'Manrope' }}>Referral Pipeline</h3>
        </div>
        <div className="relative">
          {/* Connector line */}
          <div className="absolute top-4 left-4 right-4 h-0.5 bg-muted z-0 hidden md:block" />
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 relative z-10">
            {stages.map((s, i) => {
              const isComplete = s.status === 'completed';
              const isActive = s.status === 'in_progress';
              return (
                <div key={s.key} className="flex flex-col items-center text-center" data-testid={`pipeline-stage-${s.key}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-1.5 border-2 transition-all ${
                    isComplete ? 'bg-emerald-500 border-emerald-500 text-white' :
                    isActive ? 'bg-primary border-primary text-white animate-pulse' :
                    'bg-muted border-muted-foreground/20 text-muted-foreground'
                  }`}>
                    {isComplete ? <CheckCircle className="h-4 w-4" /> :
                     isActive ? <RefreshCw className="h-3.5 w-3.5" /> :
                     <span className="text-xs font-bold">{i + 1}</span>}
                  </div>
                  <p className={`text-[11px] font-medium leading-tight ${isComplete ? 'text-emerald-700' : isActive ? 'text-primary' : 'text-muted-foreground'}`}>
                    {s.label}
                  </p>
                  {s.date && (
                    <p className="text-[9px] text-muted-foreground mt-0.5">{fmt(s.date)}</p>
                  )}
                  <p className="text-[9px] text-muted-foreground/70 mt-0.5 max-w-[100px]">{s.detail}</p>
                </div>
              );
            })}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================
   PARTICIPANT DETAIL VIEW
   ============================*/
function ParticipantDetail({ participantId, participantName, headers, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [digest, setDigest] = useState(null);
  const [digestDays, setDigestDays] = useState(7);
  const [digestLoading, setDigestLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetch(`${API}/api/referral-dashboard/participant/${participantId}`, { headers, credentials: 'include' })
      .then(r => r.json())
      .then(d => setData(d))
      .catch(() => toast.error('Failed to load participant data'))
      .finally(() => setLoading(false));
  }, [participantId]);

  const loadDigest = async (days) => {
    setDigestLoading(true);
    setDigestDays(days);
    try {
      const res = await fetch(`${API}/api/referral-dashboard/digest/${participantId}?days=${days}`, { headers, credentials: 'include' });
      if (res.ok) setDigest(await res.json());
    } catch (e) { toast.error('Failed to load digest'); }
    setDigestLoading(false);
  };

  const downloadReport = async () => {
    setReportLoading(true);
    try {
      const res = await fetch(`${API}/api/referral-dashboard/participant/${participantId}/report-pdf`, {
        headers: { Authorization: headers.Authorization }
      });
      if (!res.ok) { toast.error('Report generation failed'); return; }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `progress_report_${participantName?.replace(/\s+/g, '_')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Report downloaded');
    } catch (e) { toast.error('Download failed'); }
    setReportLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  if (!data) return <p className="text-muted-foreground text-center py-12">Unable to load data.</p>;

  const p = data.participant;
  const comp = data.compliance;
  const ci = data.checkins;
  const latestCore8 = data.core8?.assessments?.[0];
  const sections = [
    { key: 'overview', label: 'Overview', icon: Eye },
    { key: 'compliance', label: 'Compliance', icon: Shield },
    { key: 'checkins', label: 'Check-Ins', icon: Activity },
    { key: 'risk', label: 'Risk Profile', icon: AlertTriangle },
    { key: 'goals', label: 'Goals', icon: Target },
    { key: 'sessions', label: 'Sessions', icon: BookOpen },
    { key: 'notes', label: 'Approved Notes', icon: FileText },
    { key: 'milestones', label: 'Milestones', icon: GraduationCap },
    { key: 'digest', label: 'Weekly Digest', icon: Newspaper },
  ];

  return (
    <div className="space-y-5" data-testid="participant-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} data-testid="back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>{p.full_name}</h2>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span className="text-xs text-muted-foreground">Enrolled {fmt(p.enrolled_date)}</span>
            {latestCore8?.overall_risk && <RiskBadge level={latestCore8.overall_risk.level} />}
            {latestCore8?.stage_of_change && (
              <Badge variant="outline" className="text-xs">{latestCore8.stage_of_change.label}</Badge>
            )}
          </div>
        </div>
      </div>

      {/* Pipeline Tracker */}
      {data.pipeline && <PipelineTracker pipeline={data.pipeline} />}

      {/* Download Report Button */}
      <div className="flex justify-end">
        <Button
          variant="outline"
          size="sm"
          onClick={downloadReport}
          disabled={reportLoading}
          className="rounded-full"
          data-testid="download-report-btn"
        >
          {reportLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
          Download Progress Report (PDF)
        </Button>
      </div>

      {/* Section tabs */}
      <div className="flex gap-1 flex-wrap bg-muted/50 rounded-xl p-1" data-testid="detail-sections">
        {sections.map(s => (
          <button
            key={s.key}
            onClick={() => setActiveSection(s.key)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
              activeSection === s.key ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
            data-testid={`section-${s.key}`}
          >
            <s.icon className="h-3.5 w-3.5" /> {s.label}
          </button>
        ))}
      </div>

      {/* Overview */}
      {activeSection === 'overview' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="overview-section">
          <MiniStat label="Check-Ins (30d)" value={ci.total_30d} sub={`${ci.rate_30d}% rate`} />
          <MiniStat label="Compliance" value={comp.total > 0 ? `${comp.rate}%` : 'N/A'} sub={`${comp.compliant}/${comp.total} compliant`} />
          <MiniStat label="Goals" value={`${data.goals.filter(g => g.status === 'completed').length}/${data.goals.length}`} sub="completed" />
          <MiniStat label="Risk Level" value={latestCore8?.overall_risk?.level?.toUpperCase() || 'N/A'} sub={latestCore8?.overall_risk ? `Score: ${latestCore8.overall_risk.composite_score}/10` : 'Unassessed'} />
          <MiniStat label="Sober Days (30d)" value={ci.sober_days_30d} sub={`of ${ci.total_30d} reported`} />
          <MiniStat label="Work Days (30d)" value={ci.work_days_30d} sub={`of ${ci.total_30d} reported`} />
          <MiniStat label="Sessions Attended" value={data.group_attendance} sub="group sessions" />
          <MiniStat label="Achievements" value={data.achievements.length} sub="earned" />
          {data.strengths_summary && (
            <div className="col-span-full">
              <Card className="border-2 border-teal-200 rounded-2xl bg-teal-50/20">
                <CardContent className="pt-4 pb-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Heart className="h-4 w-4 text-teal-600" />
                    <h4 className="text-sm font-semibold text-teal-700">Strengths Summary</h4>
                  </div>
                  <p className="text-sm text-foreground">{data.strengths_summary.strengths_text}</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* Compliance */}
      {activeSection === 'compliance' && (
        <div className="space-y-4" data-testid="compliance-section">
          {comp.total === 0 ? (
            <EmptyState text="No compliance records yet." />
          ) : (
            <>
              <div className="flex items-center gap-4 mb-2">
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium">Overall Compliance</span>
                    <span className="text-sm font-bold">{comp.rate}%</span>
                  </div>
                  <Progress value={comp.rate} className="h-3" />
                  <p className="text-xs text-muted-foreground mt-1">{comp.compliant} of {comp.total} checks compliant</p>
                </div>
              </div>

              {/* Breakdown by type */}
              <h4 className="text-sm font-semibold text-foreground">Breakdown by Category</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(comp.by_type).map(([type, info]) => (
                  <Card key={type} className="border-0 shadow-sm rounded-xl" data-testid={`comp-type-${type}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium capitalize">{type.replace(/_/g, ' ')}</span>
                        <span className={`text-sm font-bold ${info.rate >= 80 ? 'text-emerald-600' : info.rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                          {info.rate}%
                        </span>
                      </div>
                      <Progress value={info.rate} className="h-2 mb-1" />
                      <p className="text-xs text-muted-foreground">{info.compliant}/{info.total} compliant</p>
                      {info.violations.length > 0 && (
                        <div className="mt-3 space-y-2">
                          <p className="text-xs font-semibold text-red-600">Violations:</p>
                          {info.violations.map((v, i) => (
                            <div key={`item-${i}`} className="bg-red-50 border border-red-100 rounded-lg p-2 text-xs">
                              <div className="flex justify-between">
                                <span className="text-red-700 font-medium capitalize">{v.status}</span>
                                <span className="text-muted-foreground">{fmt(v.date)}</span>
                              </div>
                              {v.notes && <p className="text-red-600 mt-1">{v.notes}</p>}
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Non-compliant details */}
              {comp.non_compliant_details.length > 0 && (
                <>
                  <h4 className="text-sm font-semibold text-red-600 mt-4">All Non-Compliant Records</h4>
                  <div className="space-y-2">
                    {comp.non_compliant_details.map((nc, i) => (
                      <Card key={`item-${i}`} className="border-red-100 shadow-sm rounded-xl">
                        <CardContent className="p-3 flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <XCircle className="h-4 w-4 text-red-500" />
                              <span className="text-sm font-medium capitalize">{nc.type?.replace(/_/g, ' ')}</span>
                            </div>
                            {nc.notes && <p className="text-xs text-muted-foreground mt-1 ml-6">{nc.notes}</p>}
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">{fmt(nc.date)}</span>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      )}

      {/* Check-Ins */}
      {activeSection === 'checkins' && (
        <div className="space-y-4" data-testid="checkins-section">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <MiniStat label="Check-Ins (30d)" value={ci.total_30d} sub={`${ci.rate_30d}% daily rate`} />
            <MiniStat label="Sober Days" value={ci.sober_days_30d} sub="last 30 days" />
            <MiniStat label="Work Days" value={ci.work_days_30d} sub="last 30 days" />
            <MiniStat label="Avg Mood" value={ci.recent.length > 0 ? (ci.recent.reduce((a, c) => a + (c.mood || 0), 0) / ci.recent.length).toFixed(1) : 'N/A'} sub="out of 10" />
          </div>
          {ci.recent.length === 0 ? (
            <EmptyState text="No check-ins recorded yet." />
          ) : (
            <div className="space-y-2">
              <h4 className="text-sm font-semibold">Recent Check-Ins</h4>
              {ci.recent.map((c, i) => (
                <Card key={`item-${i}`} className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                          (c.mood || 5) >= 7 ? 'bg-emerald-100 text-emerald-700' : (c.mood || 5) >= 4 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                        }`}>{c.mood || '—'}</div>
                        <div>
                          <div className="flex gap-2">
                            {c.stayed_sober && <Badge className="bg-emerald-50 text-emerald-600 text-xs">Sober</Badge>}
                            {c.attended_work && <Badge className="bg-blue-50 text-blue-600 text-xs">Worked</Badge>}
                            {!c.stayed_sober && c.stayed_sober !== undefined && <Badge className="bg-red-50 text-red-600 text-xs">Substance Use</Badge>}
                          </div>
                          {c.notes && <p className="text-xs text-muted-foreground mt-0.5">{c.notes}</p>}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0">{fmt(c.date)}</span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Risk Profile */}
      {activeSection === 'risk' && (
        <div className="space-y-4" data-testid="risk-section">
          {data.core8.assessments.length === 0 ? (
            <EmptyState text="No CORE-8 assessments completed yet." />
          ) : (
            <>
              {latestCore8 && (
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader className="pb-2"><CardTitle className="text-base">Latest Assessment</CardTitle></CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center justify-between">
                      <RiskBadge level={latestCore8.overall_risk?.level} />
                      <span className="text-xs text-muted-foreground">Completed {fmt(latestCore8.completed_at)}</span>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-muted rounded-xl p-3 text-center">
                        <span className="text-xs text-muted-foreground">Composite</span>
                        <p className="text-xl font-bold">{latestCore8.overall_risk?.composite_score}/10</p>
                      </div>
                      <div className="bg-muted rounded-xl p-3 text-center">
                        <span className="text-xs text-muted-foreground">Highest</span>
                        <p className="text-xl font-bold">{latestCore8.overall_risk?.highest_score}/10</p>
                      </div>
                    </div>
                    {latestCore8.risk_scores && (
                      <div className="space-y-3">
                        {Object.entries(latestCore8.risk_scores).map(([k, v]) => (
                          <div key={k}>
                            <div className="flex justify-between text-xs mb-1">
                              <span className="text-foreground">{v.label}</span>
                              <span className={v.score <= 3 ? 'text-emerald-600' : v.score <= 5 ? 'text-amber-600' : v.score <= 7 ? 'text-orange-600' : 'text-red-600'}>
                                {v.score}/10
                              </span>
                            </div>
                            <div className="w-full bg-muted rounded-full h-2">
                              <div className={`h-2 rounded-full ${v.score <= 3 ? 'bg-emerald-500' : v.score <= 5 ? 'bg-yellow-500' : v.score <= 7 ? 'bg-orange-500' : 'bg-red-500'}`} style={{ width: `${(v.score/10)*100}%` }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {latestCore8.stage_of_change && (
                      <div className="bg-primary/5 rounded-xl p-3">
                        <span className="text-xs text-muted-foreground">Stage of Change</span>
                        <p className="font-semibold text-primary">{latestCore8.stage_of_change.label}</p>
                        <p className="text-xs text-muted-foreground">{latestCore8.stage_of_change.description}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {data.risk_events.length > 0 && (
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader className="pb-2"><CardTitle className="text-base">Risk Events</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {data.risk_events.slice(0, 10).map((e, i) => (
                      <div key={`item-${i}`} className="flex items-start gap-3 bg-muted/30 rounded-lg p-3">
                        <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${e.new_score > e.old_score ? 'text-red-500' : 'text-emerald-500'}`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium capitalize">{e.category}</span>
                            <span className="text-xs text-muted-foreground">{e.old_score} → {e.new_score}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">{e.reason}</p>
                        </div>
                        <span className="text-xs text-muted-foreground shrink-0">{fmt(e.timestamp)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {data.core8.assessments.length > 1 && (
                <Card className="border-0 shadow-sm rounded-2xl">
                  <CardHeader className="pb-2"><CardTitle className="text-base">Assessment History</CardTitle></CardHeader>
                  <CardContent className="space-y-2">
                    {data.core8.assessments.map((a, i) => (
                      <div key={`item-${i}`} className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <RiskBadge level={a.overall_risk?.level} />
                          <span className="text-sm">Composite: {a.overall_risk?.composite_score}/10</span>
                        </div>
                        <span className="text-xs text-muted-foreground">{fmt(a.completed_at)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* Goals */}
      {activeSection === 'goals' && (
        <div className="space-y-4" data-testid="goals-section">
          {data.goals.length === 0 ? (
            <EmptyState text="No goals set yet." />
          ) : (
            <>
              <div className="flex gap-3 mb-2">
                <MiniStat label="Active" value={data.goals.filter(g => g.status !== 'completed').length} />
                <MiniStat label="Completed" value={data.goals.filter(g => g.status === 'completed').length} />
                <MiniStat label="Total" value={data.goals.length} />
              </div>
              <div className="space-y-2">
                {data.goals.map((g, i) => (
                  <Card key={`item-${i}`} className="border-0 shadow-sm rounded-xl">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2">
                          {g.status === 'completed' ? (
                            <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" />
                          ) : (
                            <Target className="h-4 w-4 text-amber-500 mt-0.5 shrink-0" />
                          )}
                          <div>
                            <p className="text-sm font-medium">{g.title}</p>
                            {g.notes && <p className="text-xs text-muted-foreground mt-0.5">{g.notes}</p>}
                            <div className="flex gap-2 mt-1">
                              <Badge variant="outline" className="text-xs capitalize">{g.status}</Badge>
                              {g.target_date && <span className="text-xs text-muted-foreground">Target: {fmt(g.target_date)}</span>}
                            </div>
                          </div>
                        </div>
                        {g.completed_at && <span className="text-xs text-emerald-600 shrink-0">Completed {fmt(g.completed_at)}</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Approved Notes */}
      {activeSection === 'notes' && (
        <div className="space-y-3" data-testid="notes-section">
          {data.approved_notes.length === 0 && data.approved_docs.length === 0 ? (
            <EmptyState text="No approved notes or documentation yet." />
          ) : (
            <>
              {data.approved_notes.map((n, i) => (
                <Card key={`item-${i}`} className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs" variant="outline">
                          <CheckCircle className="h-3 w-3 mr-1" /> Approved
                        </Badge>
                        <Badge variant="secondary" className="text-xs capitalize">{n.session_type || 'Session'}</Badge>
                      </div>
                      <span className="text-xs text-muted-foreground">{fmt(n.created_at)}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                      {n.subjective && <FieldBlock label="Observations" value={n.subjective} />}
                      {n.objective && <FieldBlock label="Engagement" value={n.objective} />}
                      {n.assessment && <FieldBlock label="Assessment" value={n.assessment} />}
                      {n.plan && <FieldBlock label="Plan" value={n.plan} />}
                    </div>
                  </CardContent>
                </Card>
              ))}
              {data.approved_docs.map((d, i) => (
                <Card key={`doc-${i}`} className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-2">
                      <Badge className="bg-blue-100 text-blue-700 text-xs" variant="outline">Peer Documentation</Badge>
                      <span className="text-xs text-muted-foreground">{fmt(d.created_at)}</span>
                    </div>
                    <p className="text-sm text-foreground">{d.summary}</p>
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      {/* Milestones */}
      {activeSection === 'milestones' && (
        <div className="space-y-3" data-testid="milestones-section">
          {data.milestones.length === 0 && data.achievements.length === 0 ? (
            <EmptyState text="No milestones or achievements yet." />
          ) : (
            <>
              {data.milestones.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-sm font-semibold">Journey Milestones</h4>
                  {data.milestones.map((m, i) => (
                    <Card key={`item-${i}`} className="border-0 shadow-sm rounded-xl">
                      <CardContent className="p-3 flex items-start gap-3">
                        <GraduationCap className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{m.title}</p>
                          {m.story && <p className="text-xs text-muted-foreground mt-0.5">{m.story}</p>}
                          <div className="flex gap-2 mt-1">
                            {m.category && <Badge variant="outline" className="text-xs capitalize">{m.category}</Badge>}
                            <span className="text-xs text-muted-foreground">{fmt(m.date)}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
              {data.achievements.length > 0 && (
                <div className="space-y-2 mt-4">
                  <h4 className="text-sm font-semibold">Achievements</h4>
                  {data.achievements.map((a, i) => (
                    <Card key={`item-${i}`} className="border-0 shadow-sm rounded-xl">
                      <CardContent className="p-3 flex items-center gap-3">
                        <CheckCircle className="h-4 w-4 text-emerald-500 shrink-0" />
                        <div className="flex-1">
                          <p className="text-sm font-medium">{a.title}</p>
                          <span className="text-xs text-muted-foreground">{a.type} — {fmt(a.date)}</span>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Group Sessions */}
      {activeSection === 'sessions' && (
        <div className="space-y-3" data-testid="sessions-section">
          {(!data.group_sessions || data.group_sessions.length === 0) ? (
            <EmptyState text="No group sessions attended yet." />
          ) : (
            <>
              <div className="flex items-center gap-2 mb-2">
                <MiniStat label="Sessions Attended" value={data.group_attendance} />
              </div>
              {data.group_sessions.map((s, i) => (
                <Card key={`item-${i}`} className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="text-sm font-semibold">{s.title || 'Group Session'}</h4>
                        <div className="flex items-center gap-2 flex-wrap mt-1">
                          {s.program_type && <Badge variant="outline" className="text-xs">{s.program_type}</Badge>}
                          {s.subtype && <Badge variant="secondary" className="text-xs">{s.subtype}</Badge>}
                          {s.facilitator_name && <span className="text-xs text-muted-foreground">Led by {s.facilitator_name}</span>}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-xs text-muted-foreground">{fmt(s.date)}</span>
                        {s.time && <p className="text-xs text-muted-foreground">{s.time}</p>}
                      </div>
                    </div>
                    {s.description && <p className="text-sm text-foreground mt-2">{s.description}</p>}
                    {s.notes && (
                      <div className="bg-muted/30 rounded-lg p-3 mt-2 border">
                        <p className="text-xs font-semibold text-muted-foreground mb-1">Session Notes</p>
                        <p className="text-sm text-foreground">{s.notes}</p>
                      </div>
                    )}
                    {s.participant_count > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">{s.participant_count} participants in session</p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </div>
      )}

      {/* Weekly Digest */}
      {activeSection === 'digest' && (
        <div className="space-y-4" data-testid="digest-section">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground">Period:</span>
            {[7, 14, 30].map(d => (
              <Button
                key={d}
                variant={digestDays === d && digest ? 'default' : 'outline'}
                size="sm"
                className="rounded-full text-xs"
                onClick={() => loadDigest(d)}
                disabled={digestLoading}
                data-testid={`digest-${d}d`}
              >
                {d} Days
              </Button>
            ))}
          </div>

          {digestLoading && (
            <div className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></div>
          )}

          {!digest && !digestLoading && (
            <Card className="border-0 shadow-sm rounded-2xl">
              <CardContent className="pt-8 pb-8 text-center">
                <Newspaper className="h-10 w-10 mx-auto mb-3 text-muted-foreground/30" />
                <p className="text-muted-foreground text-sm">Select a time period above to generate the digest.</p>
              </CardContent>
            </Card>
          )}

          {digest && !digestLoading && (
            <>
              {/* Alerts */}
              {digest.alerts?.length > 0 && (
                <div className="space-y-2">
                  {digest.alerts.map((a, i) => (
                    <div key={`item-${i}`} className={`flex items-start gap-2 p-3 rounded-xl border text-sm ${
                      a.level === 'critical' ? 'bg-red-50 border-red-200 text-red-700' :
                      a.level === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-700' :
                      'bg-blue-50 border-blue-200 text-blue-700'
                    }`}>
                      <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                      <span>{a.text}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Stats grid */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <MiniStat label="Check-Ins" value={digest.checkins.total} sub={`${digest.checkins.rate}% rate`} />
                <MiniStat label="Sober Days" value={digest.checkins.sober_days} />
                <MiniStat label="Work Days" value={digest.checkins.work_days} />
                <MiniStat label="Avg Mood" value={digest.checkins.avg_mood || 'N/A'} sub="out of 10" />
                <MiniStat label="Compliance" value={digest.compliance.rate != null ? `${digest.compliance.rate}%` : 'N/A'} sub={`${digest.compliance.compliant}/${digest.compliance.total}`} />
              </div>

              {/* Compliance violations */}
              {digest.compliance.violations?.length > 0 && (
                <Card className="border-red-100 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-red-600 mb-2">Compliance Violations ({digest.compliance.violations.length})</h4>
                    {digest.compliance.violations.map((v, i) => (
                      <div key={`item-${i}`} className="flex justify-between bg-red-50 rounded-lg p-2 mb-1 text-xs">
                        <span className="text-red-700 capitalize">{v.type?.replace(/_/g, ' ')}: {v.notes || 'No detail'}</span>
                        <span className="text-muted-foreground">{fmt(v.date)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Risk events */}
              {digest.risk_events?.length > 0 && (
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Risk Events ({digest.risk_events.length})</h4>
                    {digest.risk_events.map((e, i) => (
                      <div key={`item-${i}`} className="flex items-start gap-2 bg-muted/30 rounded-lg p-2 mb-1">
                        <AlertTriangle className={`h-3.5 w-3.5 mt-0.5 ${e.new_score > e.old_score ? 'text-red-500' : 'text-emerald-500'}`} />
                        <div className="flex-1 text-xs">
                          <span className="font-medium capitalize">{e.category}</span>
                          <span className="text-muted-foreground ml-1">{e.old_score} → {e.new_score}</span>
                          <p className="text-muted-foreground">{e.reason}</p>
                        </div>
                        <span className="text-xs text-muted-foreground">{fmt(e.timestamp)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Goals activity */}
              {(digest.goals.completed?.length > 0 || digest.goals.created?.length > 0) && (
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Goals Activity</h4>
                    {digest.goals.completed?.map((g, i) => (
                      <div key={`c-${i}`} className="flex items-center gap-2 text-xs mb-1.5">
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-500" />
                        <span className="font-medium">{g.title}</span>
                        <span className="text-muted-foreground ml-auto">completed {fmt(g.completed_at)}</span>
                      </div>
                    ))}
                    {digest.goals.created?.map((g, i) => (
                      <div key={`n-${i}`} className="flex items-center gap-2 text-xs mb-1.5">
                        <Target className="h-3.5 w-3.5 text-amber-500" />
                        <span className="font-medium">{g.title}</span>
                        <span className="text-muted-foreground ml-auto">created {fmt(g.created_at)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Sessions */}
              {digest.sessions?.length > 0 && (
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Sessions Attended ({digest.sessions.length})</h4>
                    {digest.sessions.map((s, i) => (
                      <div key={`item-${i}`} className="bg-muted/30 rounded-lg p-2 mb-1.5 text-xs">
                        <div className="flex justify-between">
                          <span className="font-medium">{s.title || 'Session'}</span>
                          <span className="text-muted-foreground">{fmt(s.date)}</span>
                        </div>
                        {s.program_type && <Badge variant="outline" className="text-[10px] mt-1">{s.program_type}</Badge>}
                        {s.notes && <p className="text-muted-foreground mt-1">{s.notes}</p>}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Notes & milestones */}
              {digest.approved_notes?.length > 0 && (
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Approved Notes ({digest.approved_notes.length})</h4>
                    {digest.approved_notes.map((n, i) => (
                      <div key={`item-${i}`} className="bg-muted/30 rounded-lg p-2 mb-1.5 text-xs">
                        <div className="flex justify-between">
                          <Badge variant="outline" className="text-[10px] capitalize">{n.session_type}</Badge>
                          <span className="text-muted-foreground">{fmt(n.created_at)}</span>
                        </div>
                        {n.assessment && <p className="mt-1">{n.assessment}</p>}
                        {n.plan && <p className="text-muted-foreground mt-0.5">Plan: {n.plan}</p>}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {digest.milestones?.length > 0 && (
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">Milestones ({digest.milestones.length})</h4>
                    {digest.milestones.map((m, i) => (
                      <div key={`item-${i}`} className="flex items-center gap-2 text-xs mb-1.5">
                        <GraduationCap className="h-3.5 w-3.5 text-primary" />
                        <span className="font-medium">{m.title}</span>
                        <span className="text-muted-foreground ml-auto">{fmt(m.date)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* CORE-8 */}
              {digest.core8_assessments?.length > 0 && (
                <Card className="border-0 shadow-sm rounded-xl">
                  <CardContent className="p-4">
                    <h4 className="text-sm font-semibold text-foreground mb-2">CORE-8 Assessments</h4>
                    {digest.core8_assessments.map((a, i) => (
                      <div key={`item-${i}`} className="flex items-center gap-2 text-xs mb-1.5">
                        <Brain className="h-3.5 w-3.5 text-primary" />
                        <RiskBadge level={a.risk_level} />
                        <span className="text-muted-foreground ml-auto">{fmt(a.completed_at)}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function MiniStat({ label, value, sub }) {
  return (
    <div className="bg-muted/30 rounded-xl p-3 text-center">
      <p className="text-lg font-bold text-foreground">{value}</p>
      <p className="text-xs text-muted-foreground">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground/70">{sub}</p>}
    </div>
  );
}

function FieldBlock({ label, value }) {
  if (!value) return null;
  return (
    <div className="bg-muted/30 rounded-lg border p-3">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm leading-relaxed">{value}</p>
    </div>
  );
}

function EmptyState({ text }) {
  return (
    <div className="text-center py-12 text-muted-foreground">
      <FileText className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
      <p className="text-sm">{text}</p>
    </div>
  );
}

/* ============================
   MAIN DASHBOARD
   ============================ */
export default function ReferralSourceDashboard() {
  const { user, token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('participants');
  const [selectedParticipant, setSelectedParticipant] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [programFilter, setProgramFilter] = useState('all');

  const headers = { 'Content-Type': 'application/json' };

  const filteredParticipants = useMemo(() => {
    let filtered = participants;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.full_name?.toLowerCase().includes(q) ||
        p.drug_of_choice?.toLowerCase().includes(q) ||
        p.charges?.toLowerCase().includes(q)
      );
    }
    if (programFilter !== 'all') {
      filtered = filtered.filter(p => {
        const programs = p.programs || [];
        if (programFilter === 'housing') return p.is_housing_resident;
        return programs.includes(programFilter);
      });
    }
    return filtered;
  }, [participants, searchQuery, programFilter]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [ovRes, pRes] = await Promise.all([
        fetch(`${API}/api/referral-dashboard/overview`, { headers, credentials: 'include' }),
        fetch(`${API}/api/referral-dashboard/participants`, { headers, credentials: 'include' }),
      ]);
      const [ovData, pData] = await Promise.all([ovRes.json(), pRes.json()]);
      setOverview(ovData);
      setParticipants(pData.participants || []);
    } catch (e) {
      toast.error('Failed to load dashboard');
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // If viewing a participant detail
  if (selectedParticipant) {
    return (
      <div className="space-y-4" data-testid="referral-source-dashboard">
        <ParticipantDetail
          participantId={selectedParticipant.id}
          participantName={selectedParticipant.full_name}
          headers={headers}
          onBack={() => setSelectedParticipant(null)}
        />
      </div>
    );
  }

  if (loading) return (
    <div className="flex items-center justify-center min-h-[60vh]" data-testid="referral-dashboard-loading">
      <Loader2 className="w-8 h-8 animate-spin text-primary" />
    </div>
  );

  const o = overview || {};
  const riskColors = { low: 'text-emerald-600', moderate: 'text-yellow-600', elevated: 'text-orange-600', high: 'text-red-600', critical: 'text-red-800' };

  return (
    <div className="space-y-6" data-testid="referral-source-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope' }}>Referral Source Dashboard</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant="outline" className="gap-1 border-indigo-200 text-indigo-700">
              <Building2 size={12} /> {user.referral_agency_name || 'Justice Partner'}
            </Badge>
            <span className="text-sm text-muted-foreground">Welcome, {user.full_name}</span>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="referral-dashboard-refresh">
          <RefreshCw size={14} className={`mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800 flex items-start gap-2" data-testid="approved-data-notice">
        <Shield size={16} className="mt-0.5 shrink-0" />
        <span>This dashboard shows <strong>approved and finalized</strong> participant data only. All notes and summaries have been reviewed by program leadership.</span>
      </div>

      {/* Clickable Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard icon={Users} label="My Participants" value={o.total_participants || 0} color="indigo"
          testId="stat-participants" onClick={() => setActiveTab('participants')} />
        <StatCard icon={Target} label="Active Referrals" value={o.active_referrals || 0} color="sky"
          testId="stat-active" onClick={() => setActiveTab('participants')} />
        <StatCard icon={FileText} label="Approved Updates" value={o.approved_updates || 0} color="violet"
          testId="stat-updates" onClick={() => setActiveTab('participants')} subtitle="Click participant to view" />
        <StatCard icon={Shield} label="Compliance Rate" value={o.compliance_rate ? `${o.compliance_rate}%` : 'N/A'} color="emerald"
          testId="stat-compliance" onClick={() => setActiveTab('participants')} subtitle={`${o.non_compliant_count || 0} non-compliant`} />
      </div>

      {/* Risk & Goals Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab('risk')} data-testid="risk-overview-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="h-4 w-4 text-red-500" />
              <h3 className="text-sm font-semibold">Risk Dashboard</h3>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 ml-auto" />
            </div>
            <div className="flex gap-2 flex-wrap">
              {Object.entries(o.risk_summary || {}).map(([level, count]) => (
                count > 0 && (
                  <div key={level} className="bg-muted/50 rounded-lg px-3 py-1.5 text-center">
                    <p className={`text-lg font-bold ${riskColors[level] || 'text-muted-foreground'}`}>{count}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{level}</p>
                  </div>
                )
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-all" onClick={() => setActiveTab('participants')} data-testid="goals-overview-card">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Target className="h-4 w-4 text-amber-500" />
              <h3 className="text-sm font-semibold">Goals & Progress</h3>
              <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/40 ml-auto" />
            </div>
            <div className="flex gap-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">{o.goals_completed || 0}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-amber-600">{(o.goals_total || 0) - (o.goals_completed || 0)}</p>
                <p className="text-xs text-muted-foreground">In Progress</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">{o.core8_completed || 0}</p>
                <p className="text-xs text-muted-foreground">Assessments</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-muted/50 border" data-testid="referral-tabs">
          <TabsTrigger value="participants" className="gap-1.5">
            <Users size={14} /> All Participants
          </TabsTrigger>
          <TabsTrigger value="risk" className="gap-1.5">
            <AlertTriangle size={14} /> Risk Overview
          </TabsTrigger>
        </TabsList>

        {/* Participants Tab */}
        <TabsContent value="participants" className="space-y-3 mt-4" data-testid="participants-tab">
          {/* Search & Filter Bar */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name, charges, or drug of choice..."
                className="pl-9 rounded-full"
                data-testid="participant-search"
              />
            </div>
            <Select value={programFilter} onValueChange={setProgramFilter}>
              <SelectTrigger className="w-[180px] rounded-full" data-testid="program-filter">
                <Filter size={14} className="mr-1" />
                <SelectValue placeholder="All Programs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Programs</SelectItem>
                <SelectItem value="HEAT">HEAT (Men)</SelectItem>
                <SelectItem value="HER">HER (Women)</SelectItem>
                <SelectItem value="Purpose Driven">Purpose Driven</SelectItem>
                <SelectItem value="housing">Our Square Housing</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <p className="text-xs text-muted-foreground">{filteredParticipants.length} of {participants.length} participants</p>

          {filteredParticipants.length === 0 ? (
            <EmptyState text={searchQuery || programFilter !== 'all' ? 'No participants match your search/filter.' : 'No participants yet. Participants referred by your agency will appear here.'} />
          ) : (
            filteredParticipants.map(p => (
              <Card
                key={p.id}
                className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md hover:border-primary/20 transition-all"
                onClick={() => setSelectedParticipant(p)}
                data-testid={`participant-row-${p.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-semibold text-foreground">{p.full_name}</h3>
                        <RiskBadge level={p.risk_level} />
                        {p.stage_of_change && <Badge variant="outline" className="text-xs">{p.stage_of_change}</Badge>}
                        {(p.programs || []).map(prog => (
                          <Badge key={prog} className={`text-[10px] border-0 ${
                            prog === 'HEAT' ? 'bg-orange-100 text-orange-700'
                            : prog === 'HER' ? 'bg-pink-100 text-pink-700'
                            : prog === 'Our Square' ? 'bg-cyan-100 text-cyan-700'
                            : 'bg-teal-100 text-teal-700'
                          }`}>{prog}</Badge>
                        ))}
                        {p.is_housing_resident && !(p.programs || []).includes('Our Square') && (
                          <Badge className="text-[10px] bg-cyan-100 text-cyan-700 border-0">Housing</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-1.5 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Calendar size={11} /> {fmt(p.enrolled_date)}</span>
                        {p.compliance_rate !== null && p.compliance_rate !== undefined && (
                          <span className="flex items-center gap-1">
                            <Shield size={11} /> Compliance: <span className={p.compliance_rate >= 80 ? 'text-emerald-600 font-medium' : p.compliance_rate >= 50 ? 'text-amber-600 font-medium' : 'text-red-600 font-medium'}>{p.compliance_rate}%</span>
                          </span>
                        )}
                        <span className="flex items-center gap-1"><Activity size={11} /> {p.checkins_30d} check-ins (30d)</span>
                        <span className="flex items-center gap-1"><Target size={11} /> {p.goals_completed}/{p.goals_total} goals</span>
                        <span className="flex items-center gap-1"><FileText size={11} /> {p.approved_notes} notes</span>
                      </div>
                      {(p.drug_of_choice || p.phase) && (
                        <div className="flex items-center gap-3 mt-1 text-[11px] text-slate-500">
                          {p.drug_of_choice && <span>DOC: {p.drug_of_choice}</span>}
                          {p.phase && <span>Phase: {p.phase}</span>}
                        </div>
                      )}
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground/40 shrink-0 ml-3" />
                  </div>
                </CardContent>
              </Card>
            ))
          )}}
        </TabsContent>

        {/* Risk Overview Tab */}
        <TabsContent value="risk" className="space-y-3 mt-4" data-testid="risk-tab">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
            {['critical', 'high', 'elevated', 'moderate', 'low'].map(level => {
              const count = participants.filter(p => p.risk_level === level).length;
              return (
                <div key={level} className={`rounded-xl p-3 text-center border ${
                  level === 'critical' ? 'bg-red-50 border-red-200' :
                  level === 'high' ? 'bg-red-50/50 border-red-100' :
                  level === 'elevated' ? 'bg-orange-50 border-orange-200' :
                  level === 'moderate' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-emerald-50 border-emerald-200'
                }`}>
                  <p className={`text-2xl font-bold ${riskColors[level]}`}>{count}</p>
                  <p className="text-xs text-muted-foreground capitalize">{level}</p>
                </div>
              );
            })}
          </div>
          {participants.filter(p => p.risk_level).sort((a, b) => {
            const order = { critical: 0, high: 1, elevated: 2, moderate: 3, low: 4 };
            return (order[a.risk_level] ?? 5) - (order[b.risk_level] ?? 5);
          }).map(p => (
            <Card
              key={p.id}
              className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-all"
              onClick={() => setSelectedParticipant(p)}
              data-testid={`risk-row-${p.id}`}
            >
              <CardContent className="p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <RiskBadge level={p.risk_level} />
                  <div>
                    <p className="text-sm font-medium">{p.full_name}</p>
                    <p className="text-xs text-muted-foreground">{p.stage_of_change || 'Stage unknown'} — Score: {p.risk_score ?? 'N/A'}/10</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Assessed {fmt(p.last_assessed)}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
                </div>
              </CardContent>
            </Card>
          ))}
          {participants.filter(p => !p.risk_level).length > 0 && (
            <>
              <h4 className="text-sm font-semibold text-muted-foreground mt-4">Unassessed</h4>
              {participants.filter(p => !p.risk_level).map(p => (
                <Card key={p.id} className="border-0 shadow-sm rounded-xl cursor-pointer hover:shadow-md transition-all" onClick={() => setSelectedParticipant(p)}>
                  <CardContent className="p-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline" className="text-xs">Unassessed</Badge>
                      <p className="text-sm font-medium">{p.full_name}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
                  </CardContent>
                </Card>
              ))}
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
